<script lang="ts">
  import { Dialog } from 'bits-ui'
  import type { User } from '../lib/types'

  interface Props {
    open: boolean
    devHints?: { email: string; password: string } | null
    onClose: () => void
    onLogin: (user: User) => void
  }

  let { open, devHints, onClose, onLogin }: Props = $props()

  let email = $state('')
  let password = $state('')
  let error = $state('')
  let isLoading = $state(false)

  $effect(() => {
    if (open) {
      error = ''
      if (devHints) {
        email = devHints.email
        password = devHints.password
      }
    }
  })

  async function handleSubmit(e: Event) {
    e.preventDefault()
    error = ''
    isLoading = true

    try {
      const response = await fetch('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, password }),
      })

      if (response.ok) {
        const data = await response.json()
        onLogin(data.user)
        email = ''
        password = ''
      } else {
        const data = await response.json()
        error = data.error || 'Login failed'
      }
    } catch (e) {
      error = 'Network error'
    }

    isLoading = false
  }
</script>

<Dialog.Root bind:open onOpenChange={(o) => !o && onClose()}>
  <Dialog.Portal>
    <Dialog.Overlay class="fixed inset-0 bg-black/70 z-50" />
    <Dialog.Content class="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[360px] rounded-lg bg-surface-2 p-8">
      <Dialog.Close class="absolute top-2 right-3 text-2xl text-text-muted hover:text-text-primary cursor-pointer bg-transparent border-none">
        &times;
      </Dialog.Close>

      <Dialog.Title class="m-0 mb-6 text-xl text-text-primary">
        Login
      </Dialog.Title>

      <form onsubmit={handleSubmit}>
        <div class="mb-4">
          <label for="email" class="block mb-2 text-sm text-text-secondary">Email</label>
          <input
            type="email"
            id="email"
            bind:value={email}
            required
            autocomplete="email"
            class="w-full py-3 px-3 border border-border-default rounded bg-surface-3 text-text-primary text-base focus:outline-none focus:border-border-strong"
          />
        </div>

        <div class="mb-4">
          <label for="password" class="block mb-2 text-sm text-text-secondary">Password</label>
          <input
            type="password"
            id="password"
            bind:value={password}
            required
            autocomplete="current-password"
            class="w-full py-3 px-3 border border-border-default rounded bg-surface-3 text-text-primary text-base focus:outline-none focus:border-border-strong"
          />
        </div>

        {#if error}
          <div class="text-red-400 text-sm mb-4">{error}</div>
        {/if}

        <div class="flex justify-end mt-6">
          <button
            type="submit"
            disabled={isLoading}
            class="py-2 px-5 border-none bg-indigo-500 text-white rounded-md cursor-pointer font-medium hover:enabled:bg-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </div>
      </form>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
